import React, { useState } from 'react';
import { Button, Chip, Stack, Typography } from '@mui/material';

import { Loader } from 'components/Loader';
import { useUnanswered } from '../api/getUnanswered';
import { formatDate, formatDateTime } from '../utils/formatDate';
import { AnswerQuestionModal } from './AnswerQuestionModal';
import { DeclineQuestionModal } from './DeclineQuestionModal';
import { PagedList, usePaging } from './PagedList';
import { QnaCard, QnaEmpty, QnaEntry } from './QnaCard';
import { QnaFiles } from './QnaFiles';

const secondaryButtonSx = ({ palette }) => ({
  boxShadow: 25,
  color: palette.primary.main,
  backgroundColor: '#fff',
});

export const UnansweredList = ({ resource, id }) => {
  const paging = usePaging();
  const { data, isLoading, isError } = useUnanswered(resource, id, paging);
  const [answering, setAnswering] = useState(null);
  const [declining, setDeclining] = useState(null);

  if (isLoading) return <Loader />;
  if (isError)
    return (
      <QnaEmpty title="Something went wrong" message="Unanswered questions could not be loaded" />
    );
  if (!data?.total) {
    return (
      <QnaEmpty
        title="No unanswered questions"
        message="New questions from suppliers will show here"
      />
    );
  }

  return (
    <PagedList paging={paging} total={data.total} sortLabel="Sorted by date asked">
      {data.items.map((question) => (
        <QnaCard
          key={question.question_id}
          title="Question"
          actions={
            <Stack direction="row" gap={1} alignItems="center">
              {question.draft_saved_on && (
                <Chip size="small" label="Draft answer" color="warning" variant="outlined" />
              )}
              <Button
                size="medium"
                variant="contained"
                onClick={() => setDeclining(question)}
                sx={secondaryButtonSx}
              >
                Decline
              </Button>
              <Button
                size="medium"
                variant="contained"
                color="primary"
                onClick={() => setAnswering(question)}
              >
                Answer
              </Button>
            </Stack>
          }
        >
          <QnaEntry
            heading={`Question submitted ${formatDate(question.created_on)}`}
            author={`${question.asked_by_name}, ${question.company_name}`}
            text={question.question}
          />
          <QnaFiles
            resource={resource}
            id={id}
            files={question.question_files}
            heading="Question attachments"
          />
          <QnaFiles
            resource={resource}
            id={id}
            files={question.answer_files}
            heading="Draft answer attachments"
          />
          {question.draft_saved_on && (
            <Typography variant="caption" color="text.secondary">
              Draft answer saved {formatDateTime(question.draft_saved_on)}
              {question.draft_saved_by_name ? ` by ${question.draft_saved_by_name}` : ''}
            </Typography>
          )}
        </QnaCard>
      ))}

      <AnswerQuestionModal
        resource={resource}
        id={id}
        question={answering}
        open={!!answering}
        handleClose={() => setAnswering(null)}
      />
      <DeclineQuestionModal
        resource={resource}
        id={id}
        question={declining}
        open={!!declining}
        handleClose={() => setDeclining(null)}
      />
    </PagedList>
  );
};
